import z from 'zod'
import { ExchangeApiConfigurationException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'

const BASE_URL = 'https://api.bybit.com'
const TIME_WINDOW = 5000 // 5秒の有効期間

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  dataString: string
  method: 'GET' | 'POST'
}): Promise<Record<string, string>> => {
  const { apiKey, apiSecret, createHeaders, dataString, method } = params

  // 署名対象文字列の作成
  const timestamp = Date.now()
  let signTarget = ''
  if (method === 'GET') {
    // GETの場合: timestamp + apiKey + TIME_WINDOW + queryString
    signTarget = `${timestamp}${apiKey}${TIME_WINDOW}${dataString}`
  } else if (method === 'POST') {
    // POSTの場合: timestamp + apiKey + TIME_WINDOW + jsonString
    signTarget = `${timestamp}${apiKey}${TIME_WINDOW}${dataString}`
  }

  // HMAC-SHA256署名の生成とhexdigest化
  const signature = await sign({
    algorithm: 'sha256',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'hex',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    'X-BAPI-API-KEY': apiKey,
    'X-BAPI-TIMESTAMP': timestamp.toString(),
    'X-BAPI-SIGN': signature,
    'X-BAPI-RECV-WINDOW': TIME_WINDOW.toString(),
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const bodyString = body ? JSON.stringify(body) : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`
      const dataString = method === 'GET' ? queryString : bodyString
      const headers = await createAuthHeaders({
        apiKey,
        apiSecret,
        createHeaders,
        dataString,
        method,
      })
      return {
        url,
        headers,
        body: method === 'POST' ? bodyString : undefined,
      }
    },
    getDelayForRetry: ({ statusCode }) => {
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    validateResponse: ({ data }) => {
      const parsed = z
        .object({
          retCode: z.number(),
          retMsg: z.string().optional(),
        })
        .safeParse(data)
      if (!parsed.success) {
        return {
          status: 'error',
          error: new Error('Invalid response format'),
        }
      }
      const { retCode, retMsg } = parsed.data
      if (retCode === 0) {
        return {
          status: 'success',
        }
      }
      // 10000: Server Timeout
      if (retCode === 10000) {
        return {
          status: 'retry',
          delay: 5000,
          error: new Error(retMsg),
        }
      }
      // 10002: The request time exceeds the time window range.
      if (retCode === 10002) {
        return {
          status: 'retry',
          delay: 1000,
          error: new Error(retMsg),
        }
      }
      // 10010: API Key の IP 許可リストに接続元が含まれていない
      if (retCode === 10010) {
        return {
          status: 'error',
          error: new ExchangeApiConfigurationException({
            reason: 'ip-not-allowed',
            detail: retMsg ?? `retCode: ${retCode}`,
          }),
        }
      }
      return {
        status: 'error',
        error: new Error(
          `Bybit API Error > retCode: ${retCode} / message: ${retMsg}`,
        ),
      }
    },
    proxy,
  })
